import React, { useEffect, useState } from 'react';
import './App.css';
import { getTelegramInitData, initTelegramWebApp } from './telegram';
import {
  AdvertiserDashboard,
  Advertisement,
  AdvertiserProfile,
  createAdvertisement,
  fetchAdvertiserAdvertisements,
  fetchAdvertiserDashboard,
  fetchAdvertiserProfile,
  loginWithTelegram,
  updateAdvertiserLanguage,
} from './api';

const App: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'dashboard' | 'ads' | 'profile'>(
    'dashboard',
  );
  const [dashboard, setDashboard] = useState<AdvertiserDashboard | null>(null);
  const [ads, setAds] = useState<Advertisement[]>([]);
  const [profile, setProfile] = useState<AdvertiserProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [form, setForm] = useState({
    title: '',
    description: '',
    category: 'FLOWERS' as Advertisement['category'],
    price: '',
    currency: 'USD',
    city: '',
    photoUrls: '',
  });

  useEffect(() => {
    const webApp = initTelegramWebApp();

    const run = async () => {
      try {
        const initData = getTelegramInitData();
        if (!initData) {
          setError('Telegram initData не найден. Откройте приложение из Telegram.');
          setLoading(false);
          return;
        }

        await loginWithTelegram(initData);
        const [dash, list, prof] = await Promise.all([
          fetchAdvertiserDashboard(),
          fetchAdvertiserAdvertisements({ page: 1, size: 10 }),
          fetchAdvertiserProfile(),
        ]);

        setDashboard(dash);
        setAds(list.advertisements);
        setProfile(prof);
      } catch (e) {
        console.error(e);
        setError('Ошибка загрузки данных из fresh-studio-core');
      } finally {
        setLoading(false);
      }
    };

    run();
  }, []);

  return (
    <div className="tg-app">
      <header className="tg-app__header">
        <div className="tg-app__header-left">
          <div className="tg-app__logo">FA</div>
          <div className="tg-app__title">
            <span className="tg-app__title-main">Fresh Ads Studio</span>
            <span className="tg-app__title-sub">Telegram рекламный кабинет</span>
          </div>
        </div>
        <div className="tg-app__actions">
          <span className="tg-chip">Live in Telegram</span>
          <div className="tg-avatar">AD</div>
        </div>
      </header>

      <div className="tg-app__content">
        <aside className="tg-sidebar">
          <div className="tg-sidebar__menu">
            <div
              className={
                activeTab === 'dashboard'
                  ? 'tg-menu-item tg-menu-item--active'
                  : 'tg-menu-item'
              }
              onClick={() => setActiveTab('dashboard')}
            >
              <div className="tg-menu-item__left">
                <div
                  className="tg-menu-item__icon"
                  style={{ background: 'rgba(46, 166, 255, 0.2)' }}
                >
                  📊
                </div>
                <span className="tg-menu-item__label">Обзор</span>
              </div>
            </div>
            <div
              className={
                activeTab === 'ads'
                  ? 'tg-menu-item tg-menu-item--active'
                  : 'tg-menu-item'
              }
              onClick={() => setActiveTab('ads')}
            >
              <div className="tg-menu-item__left">
                <div
                  className="tg-menu-item__icon"
                  style={{ background: 'rgba(121, 134, 203, 0.32)' }}
                >
                  🎯
                </div>
                <span className="tg-menu-item__label">Кампании</span>
              </div>
            </div>
            <div
              className={
                activeTab === 'profile'
                  ? 'tg-menu-item tg-menu-item--active'
                  : 'tg-menu-item'
              }
              onClick={() => setActiveTab('profile')}
            >
              <div className="tg-menu-item__left">
                <div
                  className="tg-menu-item__icon"
                  style={{ background: 'rgba(255, 193, 7, 0.24)' }}
                >
                  ⚙️
                </div>
                <span className="tg-menu-item__label">Профиль и язык</span>
              </div>
            </div>
          </div>
          <div className="tg-sidebar__footer">
            fresh-studio-core · Telegram Mini App
          </div>
        </aside>

        <main className="tg-main">
          <div className="tg-main__header">
            <div className="tg-main__title">
              <span className="tg-main__title-primary">
                {activeTab === 'dashboard' && 'Дашборд рекламодателя'}
                {activeTab === 'ads' && 'Мои объявления'}
                {activeTab === 'profile' && 'Профиль и настройки'}
              </span>
              <span className="tg-main__title-secondary">
                {activeTab === 'dashboard' &&
                  'Общая статистика по объявлениям в Telegram'}
                {activeTab === 'ads' &&
                  'Управляйте объявлениями и создавайте новые кампании'}
                {activeTab === 'profile' &&
                  'Данные рекламодателя и предпочитаемый язык'}
              </span>
            </div>
            <div className="tg-main__actions">
              {activeTab === 'ads' && (
                <button
                  className="tg-btn tg-btn--primary"
                  onClick={() => {
                    setCreateError(null);
                    setCreating((prev) => !prev);
                  }}
                >
                  + Новая кампания
                </button>
              )}
            </div>
          </div>

          <div className="tg-main__body">
            {loading && (
              <section className="tg-stats">
                <div className="tg-card">
                  <div className="tg-card__label">Загрузка данных...</div>
                  <div className="tg-card__value">···</div>
                  <div className="tg-card__hint">Подключение к fresh-studio-core</div>
                </div>
              </section>
            )}

            {error && !loading && (
              <section className="tg-stats">
                <div className="tg-card">
                  <div className="tg-card__label">Ошибка</div>
                  <div className="tg-card__value">—</div>
                  <div className="tg-card__hint">{error}</div>
                </div>
              </section>
            )}

            {!loading && !error && dashboard && activeTab === 'dashboard' && (
              <section className="tg-stats">
                <div className="tg-card">
                  <div className="tg-card__label">Всего объявлений</div>
                  <div className="tg-card__value">
                    {dashboard.totalAdvertisements}
                  </div>
                  <div className="tg-card__hint">
                    Опубликовано: {dashboard.publishedCount}
                  </div>
                </div>
                <div className="tg-card">
                  <div className="tg-card__label">На модерации</div>
                  <div className="tg-card__value">{dashboard.pendingCount}</div>
                  <div className="tg-card__hint">
                    Одобрено: {dashboard.approvedCount}
                  </div>
                </div>
                <div className="tg-card">
                  <div className="tg-card__label">Отклонено</div>
                  <div className="tg-card__value">{dashboard.rejectedCount}</div>
                  <div className="tg-card__hint">Требует доработки</div>
                </div>
              </section>
            )}

            {activeTab === 'ads' && (
              <section>
                <div className="tg-section-title">Кампании</div>

              {creating && (
                <div className="tg-card" style={{ marginBottom: 8 }}>
                  <div className="tg-section-title">Новое объявление</div>
                  <div style={{ display: 'grid', gap: 6, fontSize: 12 }}>
                    <input
                      className="tg-input"
                      placeholder="Заголовок"
                      value={form.title}
                      onChange={(e) =>
                        setForm((f) => ({ ...f, title: e.target.value }))
                      }
                    />
                    <textarea
                      className="tg-input"
                      placeholder="Описание"
                      rows={3}
                      value={form.description}
                      onChange={(e) =>
                        setForm((f) => ({ ...f, description: e.target.value }))
                      }
                    />
                    <div style={{ display: 'flex', gap: 6 }}>
                      <select
                        className="tg-input"
                        value={form.category}
                        onChange={(e) =>
                          setForm((f) => ({
                            ...f,
                            category: e.target.value as Advertisement['category'],
                          }))
                        }
                      >
                        <option value="FLOWERS">Цветы</option>
                        <option value="COSMETICS">Косметика</option>
                        <option value="JEWELRY">Украшения</option>
                      </select>
                      <input
                        className="tg-input"
                        style={{ maxWidth: 120 }}
                        placeholder="Город"
                        value={form.city}
                        onChange={(e) =>
                          setForm((f) => ({ ...f, city: e.target.value }))
                        }
                      />
                    </div>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <input
                        className="tg-input"
                        style={{ maxWidth: 140 }}
                        placeholder="Цена"
                        value={form.price}
                        onChange={(e) =>
                          setForm((f) => ({ ...f, price: e.target.value }))
                        }
                      />
                      <input
                        className="tg-input"
                        style={{ maxWidth: 100 }}
                        placeholder="Валюта"
                        value={form.currency}
                        onChange={(e) =>
                          setForm((f) => ({ ...f, currency: e.target.value }))
                        }
                      />
                    </div>
                    <input
                      className="tg-input"
                      placeholder="Фото (URL через запятую)"
                      value={form.photoUrls}
                      onChange={(e) =>
                        setForm((f) => ({ ...f, photoUrls: e.target.value }))
                      }
                    />
                    {createError && (
                      <div style={{ color: '#ff8a80' }}>{createError}</div>
                    )}
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        gap: 8,
                      }}
                    >
                      <button
                        className="tg-btn"
                        onClick={() => setCreating(false)}
                        type="button"
                      >
                        Отмена
                      </button>
                      <button
                        className="tg-btn tg-btn--primary"
                        type="button"
                        onClick={async () => {
                          try {
                            setCreateError(null);
                            const priceNum = Number(form.price);
                            if (!form.title || !form.description || !priceNum) {
                              setCreateError(
                                'Заполните заголовок, описание и корректную цену',
                              );
                              return;
                            }
                            const photos = form.photoUrls
                              .split(',')
                              .map((p) => p.trim())
                              .filter(Boolean);

                            await createAdvertisement({
                              title: form.title,
                              description: form.description,
                              category: form.category,
                              price: priceNum,
                              currency: form.currency,
                              city: form.city || null,
                              freshUntil: null,
                              photoUrls: photos,
                            });

                            const [dash, list] = await Promise.all([
                              fetchAdvertiserDashboard(),
                              fetchAdvertiserAdvertisements({ page: 1, size: 10 }),
                            ]);

                            setDashboard(dash);
                            setAds(list.advertisements);
                            setCreating(false);
                          } catch (e) {
                            console.error(e);
                            setCreateError('Не удалось создать объявление');
                          }
                        }}
                      >
                        Создать
                      </button>
                    </div>
                  </div>
                </div>
              )}

                <div className="tg-campaigns">
                  <div className="tg-campaigns__row tg-campaigns__row--header">
                    <span>Название</span>
                    <span>Статус</span>
                    <span>Город</span>
                    <span>Цена</span>
                  </div>

                  {loading && (
                    <div className="tg-campaigns__row">
                      <span>Загрузка кампаний...</span>
                      <span>· · ·</span>
                      <span>· · ·</span>
                      <span>· · ·</span>
                    </div>
                  )}

                  {!loading && !error && ads.length === 0 && (
                    <div className="tg-campaigns__row">
                      <span>У вас пока нет объявлений</span>
                      <span>
                        <span className="tg-pill">
                          <span className="tg-pill-dot" />
                          <span className="tg-pill-label">Создайте первое</span>
                        </span>
                      </span>
                      <span>—</span>
                      <span>—</span>
                    </div>
                  )}

                  {!loading &&
                    !error &&
                    ads.map((ad) => (
                      <div key={ad.id} className="tg-campaigns__row">
                        <span>{ad.title}</span>
                        <span>
                          <span
                            className={
                              ad.status === 'PUBLISHED'
                                ? 'tg-pill tg-pill--success'
                                : 'tg-pill'
                            }
                          >
                            <span className="tg-pill-dot" />
                            <span className="tg-pill-label">{ad.status}</span>
                          </span>
                        </span>
                        <span>{ad.city || '—'}</span>
                        <span>
                          {ad.price}{' '}
                          <span style={{ opacity: 0.7 }}>
                            {ad.currency || 'USD'}
                          </span>
                        </span>
                      </div>
                    ))}
                </div>
              </section>
            )}

            {activeTab === 'profile' && profile && (
              <section>
                <div className="tg-card">
                  <div className="tg-section-title">Профиль</div>
                  <div style={{ fontSize: 13, display: 'grid', gap: 4 }}>
                    <div>
                      <strong>Имя:</strong>{' '}
                      {profile.firstName || profile.lastName
                        ? `${profile.firstName ?? ''} ${
                            profile.lastName ?? ''
                          }`.trim()
                        : '—'}
                    </div>
                    <div>
                      <strong>Telegram:</strong>{' '}
                      {profile.telegramUsername
                        ? `@${profile.telegramUsername}`
                        : profile.telegramUserId || '—'}
                    </div>
                    <div>
                      <strong>Email:</strong> {profile.email || '—'}
                    </div>
                    <div>
                      <strong>Язык интерфейса:</strong>{' '}
                      {profile.languageCode || 'не указан'}
                    </div>
                  </div>
                </div>

                <div className="tg-card" style={{ marginTop: 8 }}>
                  <div className="tg-section-title">Изменить язык</div>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <select
                      className="tg-input"
                      style={{ maxWidth: 180 }}
                      value={profile.languageCode ?? 'ru'}
                      onChange={async (e) => {
                        const code = e.target.value;
                        try {
                          await updateAdvertiserLanguage(code);
                          setProfile((prev) =>
                            prev ? { ...prev, languageCode: code } : prev,
                          );
                        } catch (err) {
                          console.error(err);
                        }
                      }}
                    >
                      <option value="ru">Русский</option>
                      <option value="en">English</option>
                      <option value="uz">O‘zbek</option>
                    </select>
                    <span style={{ fontSize: 12, opacity: 0.75 }}>
                      Настройка используется в боте и интерфейсе
                    </span>
                  </div>
                </div>
              </section>
            )}
          </div>

          <footer className="tg-footer">
            <span>Все данные демо-режима · API fresh-studio-core</span>
            <div className="tg-footer__right">
              <span className="tg-dot" />
              <span>Подключено к Telegram WebApp</span>
            </div>
          </footer>
        </main>
      </div>
    </div>
  );
};

export default App;
